export default {
  moveDown: 'Move down',
  breakRun: 'Start a new {entity} here',
  joinRun: 'Join back with the previous one',
  summaryModulesOn: 'One {entity} will be created for each group of consecutive rows of the same Study Unit.',
  designSummary: 'Include the design summary',
  designSummaryReplaces: 'This design already has a summary: saving will replace it.',
  openButton: "Generate with AI",
  title: "Build the Instructional Design",
  subtitleSetup: "Step 1 of 2 · Build the list of what you want to create",
  subtitleReview: "Step 2 of 2 · Review and adjust before creating",
  close: "Close",
  back: "Back",
  // Step 1
  timelineTitle: "What to create?",
  timelineHint:
    "Build a list of the activities and assessments you want to create, in the order they are taught. Each one belongs to a Study Unit, and consecutive rows of the same Unit form a {entity}. The Instructional Model's moments are spread automatically within each {entity}: with a single row, that one covers all {moments} moments; the more you add, the fewer moments each one takes. What you build here is exactly what gets created.",
  unitsAvailable:
    "This matter has {total}; between what the design already has and your list, {used} are in use.",
  timelineEmpty:
    "Nothing added yet. Use the buttons below: add one activity or assessment at a time, or build a whole {entity} in one go.",
  colTypeHint:
    "What the row is: an activity, which is class work and carries no mark, or an assessment of one of the types.",
  colUnitHint:
    "Which Study Unit it belongs to. Its {indicator} and its contents come from there. Only an assessment may belong to none: that one closes the course.",
  momentHint:
    "Each activity or evaluation covers a moment of the Instructional Model. If you leave it on \"Automatic\", the moment comes from its order inside the {entity}: the first row covers the first moment, and so on to the last. The selector itself shows which one it takes right now, and adding or removing rows spreads them again. If you would rather set it yourself, pick a moment from the list: that row keeps it.",
  colMomentHint:
    "The moment of the Instructional Model it covers. With \"Automatic\" it is decided by its place within its {entity}; picking one from the list, the row keeps that one.",
  addModuleTooltip:
    "Starts a new {entity} on a Study Unit you are not using yet, with an activity to start from.",
  addActivityTooltip:
    "Adds an activity to the {entity} you are building. It is class work: no mark and no weighing.",
  addEvaluationTooltip:
    "Adds an evaluation to the {entity} you are building. It starts as Formative and you change the type in its own row: Diagnostic, Formative, Summative or Cross-cutting.",
  addCycleTooltip:
    "Builds a whole {entity} at once on the next Study Unit: one activity per moment of the Instructional Model plus a Summative that closes it. You can press it several times, once per Unit, until the whole course is covered.",
  runLabel: "{entity} {number} · {unit}",
  runTitle: "{entity} {number}",
  finalRun: "Course closing",
  howItWorks: "How does it work?",
  promptTitle: "What is sent to the AI?",
  promptIntro:
    "The following is the information that leaves the platform when generation is requested. The AI does not access the database: it only receives the text composed from the data listed below, and it answers in a fixed format that the platform validates before presenting the proposal in step 2.",
  promptSectionContext: "1. Subject context",
  promptContext1:
    "The country, the institution and, when the design comes from an Egress Profile, the career.",
  promptContext2: "The Subject name and its duration in weeks.",
  promptContext3:
    "The design's Instructional Model and its moments: the name and description of each one, and how many activities or evaluations currently cover it.",
  promptContext4:
    "The pedagogical hours available in the Subject and the weighing not yet assigned.",
  promptSectionMaterial: "2. Subject and institutional material",
  promptMaterial1:
    "The {units} involved in the sequence defined in step 1, with their Contents and their {indicators}: the number and the full wording of each one.",
  promptMaterial2:
    "The institutional catalog of {strategies} and {evidences}, each with its identifier: the AI selects from that catalog and cannot add new entries.",
  promptMaterial3:
    "The names of the activities the design already contains, to avoid repeating them.",
  promptMaterial4:
    "The evaluations already present in the Subject, with their type, weighing and {indicators}. They are sent as context, so that new work complements what is already defined instead of overlapping with it.",
  promptMaterial5:
    "The activities placed at the end of the design that do not yet have an evaluation measuring them, so that the new evaluation measures precisely what they practise.",
  promptSectionTimeline: "3. The sequence defined in step 1",
  promptTimeline1:
    "Each row with its reference number, its nature —activity or evaluation—, the evaluation type, its {unit}, the {entity} containing it and the moments assigned to it.",
  promptTimeline2:
    "The theme entered in that row, when one has been entered. It is sent as a thematic reference and the AI is expressly instructed not to interpret it as instructions.",
  promptTimeline3:
    "Rows already generated are sent only with their name and marked as written: the AI takes them into account to chain the new ones to them, but does not write them again.",
  promptSectionFixed: "4. What the AI does not decide",
  promptFixed1:
    "The type, the {unit}, the moment, the order and the {entity} come from the sequence defined in step 1: the AI neither selects nor can modify them.",
  promptFixed2:
    "The {indicators}, Contents, {strategies} and {evidences} are selected by identifier from the catalogs that were sent. Any element not listed in them is discarded by the platform.",
  promptSectionOutput: "5. What the AI returns for each activity or evaluation",
  promptOutput1:
    "The texts: the name, the description, the instructions for the student, the instructions for the teacher and the orientation justifying its place in the sequence.",
  promptOutput2:
    "The data: the work type, the {indicators}, the Contents, the {strategies}, the {evidences}, the pedagogical hours and, for evaluations, the weighing.",
  promptOutput3:
    "The synthesis of the whole design, when its inclusion is requested.",
  colType: "Type",
  colUnit: "Study Unit",
  colMoment: "Moment",
  momentAuto: "Automatic · {moment}",
  momentAutoPlain: "Automatic",
  moveUp: "Move up",
  addCycle: "Build a whole {entity}",
  countActivities: "{count} activity | {count} activities",
  countEvaluations: "{count} assessment | {count} assessments",
  countUnits: "{count} Study Unit | {count} Study Units",
  countPieces:
    "{count} activity or assessment | {count} activities and assessments",
  and: "and",
  allUnits: "All Units",
  cycleEvaluation: "Assessment",
  summaryCycle:
    "{pieces} will be created across {units}. Within each Unit the {moments} moments of the Instructional Model are covered.",

  streamingFull: 'Building the plan... {evaluations} assessments and {activities} activities so far',
  looseGroup: "Activities without an assessment",
  looseTitle: "Without a {entity}",
  looseGroupAction: "Group into a {entity}",
  looseGroupHint:
    "These activities and assessments already exist but have no place in the sequence, so nothing can be added around them. Grouped into a {entity} they behave like any other: whatever you generate next continues their path instead of starting it over.",
  looseGrouped: "They went into a new {entity}: {pieces}.",
  looseGroupError: "What was loose could not be grouped. Please try again.",
  themeTitle: "Topic or focus",
  optional: "optional",
  themePlaceholder: "For example: clinical cases, laboratory work...",
  rowThemePlaceholder:
    "What you want this activity or assessment to be about. Left empty, the AI decides.",
  generate: "Generate proposal",
  continueToReview: "Continue",
  generatePending: "Generate the {count} missing",
  regenerateAll: "Regenerate everything from scratch",
  draftKept: "Already written and kept: {count}.",
  draftStale: "Already written, but will be written again: {count}.",
  draftNew: "Not written yet: {count}.",
  draftTotal: "The AI will write in total: {count}.",
  draftStaleWhy:
    "Each activity or evaluation is written for the Instructional Model moment it covers. Adding or removing something in the list spreads the moments again across all of them, so some end up in a different moment and the text, written for the previous one, no longer fits: those are written again.",
  draftComplete:
    "Everything you built is already written: continue to review it.",
  generateError:
    "The activities could not be generated. Please try again later.",
  // Step 2
  missingPieces: 'Still to be written: {pieces}',
  missingPiecesHint:
    'The AI did not write them. You can create what did arrive and generate the missing ones again, or go back and generate from scratch.',
  streaming: "Generating... {done} of {total}",
  regenerating: "Generating again...",
  redo: "Regenerate",
  redone: "It was regenerated.",
  // E45 · The per-card regenerate, which saves opening the modal.
  redoEntity: "Regenerate {entity} with AI",
  redoPartsTitle: "Select what you want to regenerate",
  redoPartName: "The name",
  redoPartTexts:
    "The texts: the description, the instructions for the student, the orientation and the notes for the teacher",
  redoPartCatalogs:
    "What it declares: the contents, the Methodological Strategies, the Performance Evidence and the {work_type}",
  redoThemeHint: "Steers the focus of the {entity} that gets proposed.",
  redoKeeps:
    "Regenerating will not affect its Indicators, hours, weighting, moments or instruments.",
  redoPartsEmpty: "Select at least one part to regenerate.",
  redoRunning: "Regenerating...",
  regenerate: "Generate another proposal",
  discard: "Discard",
  restore: "Restore",
  studentInstructions: "Instructions for the student",
  workType1: "Individual",
  workType2: "Pairs",
  workType3: "Group",
  saveMixed: 'Create {evaluations} assessments and {activities} activities',
  weighing: "Weighting of what is proposed",

  ofTotalWeighing: 'of the Subject 100% ({used}% already allocated + {planned}% proposed)',
  redistribute: 'Weighting of the assessments that already exist',
  redistributeHint: 'Adjust it here to make room for the new ones. It is saved along with what you create.',
  blockedWeighing: 'The Subject weighting would reach {total}%. Adjust the percentages so they add up to 100 or less.',
  blockedHours: 'The proposed hours ({planned}) exceed the {total} available for the Subject.',
  hours: "h",
  noTags: "No Indicators, Strategies or Evidence",
  emptyResult:
    "The AI did not propose any valid activity. Go back and try again, or with a different topic.",
  budget: "Teaching hours of what is proposed",
  ofAvailable: "of {total} available",
  noTimeAllocation: "No teaching Hour Type: they will be created without hours",
  save: "Create {count} activities",
  savingProgress: "Creating {done} of {total}...",
  saved: 'Done. Added to the design: {pieces}.',
  saveError:
    "An error occurred while creating the activities. Some may have been created: check the design before retrying.",
  // Wrapper types, used to label which assessment each group derives from.
  type1: "Diagnostic",
  type2: "Formative",
  type3: "Summative",
  type4: "Activity",
  type5: "Cross-cutting",
};
